/**
 * SocialProOrderPicker — lets an agent pick which WMS property a script is for.
 * Only orders carrying the Social Pro package are eligible (talks back to WMS).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Building2, Check, Loader2, Lock, RefreshCw, ScrollText } from "lucide-react";
import "./SocialProOrderPicker.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useSocialProOrders(enabled) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/social-pro/eligible-orders`);
      setData(r.data);
    } catch (_) {
      setData({ linked: false, gating_enabled: false, orders: [], all_orders: [] });
    } finally {
      setLoading(false);
    }
  }, [enabled]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export default function SocialProOrderPicker({ data, loading, onReload, value, onChange }) {
  if (!data || (!data.linked && !data.placeholder)) return null;
  const gating = !!data.gating_enabled;
  const eligible = data.orders || [];
  const options = gating ? eligible : (eligible.length ? eligible : (data.all_orders || []));
  const showingFallback = !gating && eligible.length === 0 && options.length > 0;

  return (
    <div className="spo" data-testid="social-pro-picker">
      <div className="spo-head">
        <Building2 size={16} />
        <div className="spo-head-text">
          <span className="spo-title">Which property is this script for?</span>
          <span className="spo-sub">
            {gating
              ? "Only properties with a Social Pro package can have a script added."
              : showingFallback
                ? "No Social Pro packages found yet — showing your recent WMS orders."
                : "Pick the WMS order so the editors know exactly which property this is."}
          </span>
        </div>
        <button type="button" className="spo-refresh" onClick={onReload} title="Refresh from WMS" data-testid="social-pro-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
      </div>

      {gating && !loading && eligible.length === 0 && (
        <div className="spo-empty" data-testid="social-pro-empty">
          <Lock size={14} />
          <span>None of your current WMS orders include a Social Pro package. Once one is ordered it will appear here automatically.</span>
        </div>
      )}

      {options.length > 0 && (
        <div className="spo-list">
          {options.map((o) => {
            const selected = value?.order_id === o.order_id;
            const scripted = !!o.script_order_id;
            return (
              <button
                key={o.order_id}
                type="button"
                className={`spo-item ${selected ? "is-selected" : ""} ${scripted ? "has-script" : ""}`}
                onClick={() => onChange(selected ? null : o)}
                data-testid={`social-pro-order-${o.order_id}`}
              >
                <div className="spo-item-main">
                  <span className="spo-address">{o.address || `Order #${o.order_id}`}</span>
                  <span className="spo-meta">
                    <span className="spo-id">WMS #{o.order_id}</span>
                    {o.matched_product && <span className="spo-pro">{o.matched_product}</span>}
                    {scripted && (
                      <span className="spo-scripted" title={`Script already ${o.script_status}`}>
                        <ScrollText size={11} /> Script {o.script_status || "sent"}{o.avatar_name ? ` · ${o.avatar_name}` : ""}
                      </span>
                    )}
                  </span>
                </div>
                <span className="spo-check">{selected && <Check size={14} />}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Full-screen first step of the Script Builder: pick the order before anything else.
export function OrderGate({ data, loading, onReload, onPick, onHistory, canSkip, onSkip }) {
  const orders = data?.orders?.length ? data.orders : (data?.all_orders || []);
  const awaiting = orders.filter((o) => !o.script_order_id);
  return (
    <div className="spo-gate" data-testid="script-order-gate">
      <div className="spo-gate-head">
        <div>
          <span className="spo-gate-eyebrow">Step 1 of 2</span>
          <h2>Which order is this script for?</h2>
          <p>{loading && !orders.length ? "Checking your orders…" : data?.placeholder
            ? "This branch isn't linked to WMS yet, so here's the demo order. Your live Social Pro orders will appear here automatically once IT switches the link on."
            : awaiting.length
              ? `${awaiting.length} Social Pro order${awaiting.length === 1 ? " is" : "s are"} still waiting for a script.`
              : "Every Social Pro order has a script — pick one to revise it, or check back when the next order lands."}</p>
        </div>
        <div className="spo-gate-actions">
          <button type="button" className="spo-refresh" onClick={onReload} title="Refresh from WMS" data-testid="script-order-gate-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          {onHistory && <button type="button" className="spo-gate-link" onClick={onHistory} data-testid="script-order-gate-history"><ScrollText size={14} /> Previous scripts</button>}
        </div>
      </div>
      {loading && !orders.length ? (
        <div className="spo-empty"><Loader2 size={14} className="animate-spin" /><span>Checking your orders…</span></div>
      ) : orders.length === 0 ? (
        <div className="spo-empty" data-testid="script-order-gate-empty"><Lock size={14} /><span>No Social Pro orders on this branch yet. Once one is placed on WMS it appears here.</span></div>
      ) : (
        <div className="spo-gate-list">
          {orders.map((o) => {
            const scripted = !!o.script_order_id;
            return (
              <button key={o.order_id} type="button" className={`spo-gate-card ${scripted ? "has-script" : "is-open"}`} onClick={() => onPick(o)} data-testid={`script-order-gate-${o.order_id}`}>
                <span className="spo-gate-status">{scripted ? <><Check size={12} /> Script {o.script_status || "sent"}</> : <>Awaiting script</>}</span>
                <span className="spo-address">{o.address || `Order #${o.order_id}`}</span>
                <span className="spo-meta">
                  <span className="spo-id">WMS #{o.order_id}</span>
                  {o.matched_product && <span className="spo-pro">{o.matched_product}</span>}
                  {o.placeholder && <span className="spo-demo">demo</span>}
                  {o.avatar_name && <span className="spo-scripted"><ScrollText size={11} /> {o.avatar_name}</span>}
                </span>
                <span className="spo-gate-cta">{scripted ? "Open & revise →" : "Write the script →"}</span>
              </button>
            );
          })}
        </div>
      )}
      {canSkip && <button type="button" className="spo-gate-skip" onClick={onSkip} data-testid="script-order-gate-skip">Continue without an order (staff only)</button>}
    </div>
  );
}
